import type { UserLocalStorage } from '../../types/localStorage';

import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import { getTestProps } from '../../lib/helpers';

import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';
import NotificationsTopbarButton from './components/NotificationsTopbarButton/NotificationsTopbarButton';
import TrialEndedPill from './components/TrialEndedPill/TrialEndedPill';

import { useNotifications } from '../../hooks/api/useNotifications';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import useSelectedSpace from '../../hooks/useSelectedSpace';

import { MagnifierSolidIcon } from '../../images/shapes';

import PageLayout from '../../layout/PageLayout/PageLayout';
import DashboardFirstSteps from './sections/DashboardFirstSteps';
import DashboardIntro from './sections/DashboardIntro';
import DashboardLatestUpdates from './sections/DashboardLatestUpdates';
import DashboardQuickLinks from './sections/DashboardQuickLinks';
import DashboardStats from './sections/DashboardStats';

const testId = 'home';

const Home = () => {
  const { t } = useTranslation();
  const { spaceLimits, spaceData } = useSelectedSpace();
  const globalSearch = useGlobalSearch({ testId });
  const [userStorage] = useLocalStorageState<UserLocalStorage>('cms.user');

  const { notifications, notificationsLoading, onNotificationsRead } =
    useNotifications();

  const todoList = Object.values(userStorage?.data?.config?.todo || {});
  const showFirstSteps =
    todoList.length === 0 || todoList.some((task) => task.status !== 'done');

  const sections = [
    <DashboardIntro key="intro" testId={testId} />,
    <DashboardStats key="stats" testId={testId} />,
    ...(showFirstSteps
      ? [<DashboardFirstSteps key="first-steps" testId={testId} />]
      : []),
    <DashboardLatestUpdates key="latest-updates" testId={testId} />,
    <DashboardQuickLinks key="quick-links" testId={testId} />,
  ];

  return (
    <PageLayout
      page="dashboard"
      title={spaceData?.name || t('Global.Dashboard')}
      buttons={
        <>
          <TrialEndedPill
            className="hidden md:flex md:ml-4"
            {...getTestProps(testId, 'desktop', 'testId')}
          />
          <div
            className={
              'flex items-center h-6 px-5 rounded-full whitespace-nowrap mx-2 md:mr-4 md:ml-0  ' +
              'font-medium text-base uppercase tracking-none leading-none border ' +
              (spaceLimits?.price === 0
                ? 'bg-slate-50 border-slate-200 text-slate-600'
                : 'bg-blue-50 border-blue-200 text-blue-600')
            }
            {...getTestProps(testId, 'space-plan-pill')}
          >
            {spaceLimits?.visible_name ?? spaceLimits?.name ?? 'Free'}
          </div>

          <TopbarButton
            tooltip={t('Global.ContentSearch.Tooltip')}
            iconPosition="start"
            iconImage={<MagnifierSolidIcon className="w-4 h-4" />}
            tooltipPlacement="leftCenter"
            phoneTooltipPlacement="leftBottom"
            onClick={globalSearch}
            additionalIconClasses="w-4 min-w-4"
            buttonColor="transparent"
            additionalClasses="w-11 h-11 xl:h-8 xl:w-8"
          />
          <NotificationsTopbarButton
            notifications={notifications}
            notificationsLoading={notificationsLoading}
            onNotificationsRead={onNotificationsRead}
            {...getTestProps(testId, 'notifications', 'testId')}
          />
        </>
      }
      buttonsMobilePlacement="top"
      testId={testId}
    >
      <TrialEndedPill
        className="md:hidden"
        {...getTestProps(testId, 'mobile', 'testId')}
      />

      <div
        className="mt-6 lg:mt-4 mb-12 space-y-6"
        {...getTestProps(testId, 'sections')}
      >
        {sections}
      </div>
    </PageLayout>
  );
};

export default Home;
